import React, { useEffect, useState } from "react";
import {
  Layout,
  Typography,
  Button,
  DatePicker,
  Input,
  notification,
  Table,
  message,
  Flex,
  Form,
  Select,
  Card,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import * as XLSX from "xlsx";
import { useSelector, useDispatch } from "react-redux";
import type { AppDispatch } from "../redux/store";

import { fetchCorporateTrip } from "src/redux/actions/CorporateTripAction";
import CustomTableLoader from "src/components/common/CustomTableLoader";
import { fetchCompany } from "src/redux/actions/bookingReportAction";
const { Title } = Typography;
const columns: any = [
  {
    title: "Booking ID",
    dataIndex: "tripId",
    key: "tripId",
    align: "center",
    fixed: "left",
    width: 110,
  },
  {
    title: "Company ID",
    dataIndex: "companyId",
    key: "companyId",
    align: "center",
    fixed: "left",
    width: 110,
  },
  {
    title: "Company Name",
    dataIndex: "companyName",
    key: "companyName",
    fixed: "left",
  },
  {
    title: "Cab ID",
    dataIndex: "cabId",
    key: "cabId",
    align: "center",
    width: 110,
  },
  {
    title: "Book Date",
    dataIndex: "bookingDate",
    key: "Date",
    align: "center",
    width: 110,
    render: (text: any) => text.split(",")[0],
  },
  {
    title: "Time",
    dataIndex: "bookingDate",
    key: "Time",
    align: "center",
    width: 110,
    render: (text: any) => {
      const timePart = text.split(",")[1]?.trim();
      if (!timePart) return "";
      const [time, ampm] = timePart.split(" ");
      const [hours, minutes] = time.split(":");
      return `${hours}:${minutes} ${ampm?.toUpperCase()}`;
    },
  },
  {
    title: "Invoice Number",
    dataIndex: "invoiceNo",
    key: "invoiceNo",
    align: "center",
  },
  {
    title: "Mobile Number",
    dataIndex: "mobileNo",
    key: "mobileNo",
    align: "center",
    width: 120,
  },
  {
    title: "Distance",
    dataIndex: "distance",
    key: "distance",
    align: "center",
    width: 80,
  },
  {
    title: "Duration",
    dataIndex: "duration",
    key: "duration",
    align: "center",
    width: 80,
  },
  {
    title: "Fare",
    dataIndex: "fare",
    key: "fare",
    align: "center",
    width: 70,
  },
  {
    title: "From Location",
    dataIndex: "fromLoc",
    key: "fromLoc",
  },
  {
    title: "To Location",
    dataIndex: "toLoc",
    key: "toLoc",
  },
  {
    title: "Goods",
    dataIndex: "goods",
    key: "goods",
  },
  {
    title: "Invoice Date",
    dataIndex: "completedDate",
    key: "completedDate",
    align: "center",
    width: 110,
    render: (text: any) => text.split(",")[0],
  },
];

const Trip: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const dataSource = useSelector((state: any) => state.CorTrip.data ?? []);
  const { totalFare, tablelength } = useSelector((state: any) => state.CorTrip);
  const loading = useSelector((state: any) => state.CorTrip.loading);
  const [searchText, setSearchText] = useState<string>("");
  const [nameFilter, setNameFilter] = useState<string>("");
  const [limit, setLimit] = useState<number>(10);
  const [skip, setSkip] = useState<number>(0);
  const { Option } = Select;
  const select = useSelector((state: any) => state.booking.comdata ?? []);
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const cityid = userData.cityid;

  useEffect(() => {
    const from = dayjs();
    const to = dayjs();

    const toDatePlusOne = to.add(1, "day");
    setFromDate(from);
    setToDate(to);

    dispatch(
      fetchCorporateTrip(
        from.format("YYYY-MM-DD"),
        toDatePlusOne.format("YYYY-MM-DD")
      )
    );

    dispatch(fetchCompany(cityid));
  }, []);

  const handleFetch = () => {
    if (fromDate && toDate) {
      const from = fromDate.format("YYYY-MM-DD");
      const toPlusOne = dayjs(toDate).add(1, "day").format("YYYY-MM-DD");
      const filter = nameFilter.trim() !== "" ? nameFilter.trim() : undefined;

      dispatch(fetchCorporateTrip(from, toPlusOne, filter));
    } else {
      message.warning("Please select both From and To dates.");
    }
  };

  const handleSearch = (value: any) => {
    setSearchText(String(value || ""));
  };

  const filteredDataSource = dataSource.filter((item: any) =>
    Object.values(item).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchText.toLowerCase())
    )
  );
  const exportToExcel = (data: any[], filename: string) => {
    if (!data || data.length === 0) return;
    const exportDate = dayjs().format("YYYY-MM-DD");
    const title = "Corporate Trip";
    const titleRow = [[`${title} - Export Date: ${exportDate}`]];
    const emptyRow = [[""]];
    const headers = columns.map((col: any) => col.title);
    const rows = data.map((item) =>
      columns.map((col: any) => item[col.dataIndex as keyof any])
    );
    const worksheetData = [...titleRow, ...emptyRow, headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "CorporateTrip");
    XLSX.writeFile(workbook, `${filename}_${exportDate}.xlsx`);
    notification.success({
      message: "Export Successful",
      description: `Excel file for "${title}" has been downloaded on ${exportDate}.`,
    });
  };
  const disableFutureDates = (current: any) => {
    return current && current > dayjs().endOf("day");
  };

  const disableToDate = (current: any) => {
    if (!fromDate) return disableFutureDates(current);
    return (
      (current && current < fromDate.startOf("day")) ||
      (current && current > dayjs().endOf("day"))
    );
  };

  const handleToday = () => {
    const from = dayjs().startOf("day");
    const to = dayjs().endOf("day").add(1, "day");

    setFromDate(from);
    setToDate(dayjs());

    dispatch(
      fetchCorporateTrip(from.format("YYYY-MM-DD"), to.format("YYYY-MM-DD"))
    );
  };

  const handleWeek = () => {
    const from = dayjs().startOf("week");
    const to = dayjs().endOf("week").add(1, "day");

    setFromDate(from);
    setToDate(to.subtract(1, "day"));

    dispatch(
      fetchCorporateTrip(from.format("YYYY-MM-DD"), to.format("YYYY-MM-DD"))
    );
  };

  const handleMonth = () => {
    const from = dayjs().startOf("month");
    const to = dayjs().endOf("month").add(1, "day");

    setFromDate(from);
    setToDate(to.subtract(1, "day"));

    dispatch(
      fetchCorporateTrip(from.format("YYYY-MM-DD"), to.format("YYYY-MM-DD"))
    );
  };

  const handlePrevious = () => {
    const from = dayjs().subtract(1, "day").startOf("day");
    const to = dayjs().subtract(1, "day").endOf("day").add(1, "day");

    setFromDate(from);
    setToDate(from);

    dispatch(
      fetchCorporateTrip(from.format("YYYY-MM-DD"), to.format("YYYY-MM-DD"))
    );
  };

  return (
    <Layout
      style={{ minHeight: "100vh", overflow: "hidden", position: "relative" }}
    >
      <Flex
        style={{
          background: "whitesmoke",
          rowGap: "20px",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Flex style={{ width: "100%" }}>
          <Flex style={{ width: "50%", justifyContent: "start" }}>
            <Title
              level={3}
              style={{
                fontSize: "35px",
                fontWeight: "bolder",
                color: "black",
                fontFamily: "serif",
                textShadow: "2px 2px 5px rgba(0, 0, 0, 0.3)",
                margin: 0,
              }}
            >
              Corporate Trip
            </Title>
          </Flex>
          <Flex style={{ width: "50%", justifyContent: "end" }}>
            <Flex
              style={{
                width: "100%",
                justifyContent: "end",
                gap: "3%",
              }}
            >
              <Typography.Text
                strong
                style={{
                  width: "30%",
                  fontSize: "20px",
                  padding: "7px",
                  backgroundColor: "#f9fafb",
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  textAlign: "center",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                }}
              >
                Trips : {tablelength || 0}
              </Typography.Text>
              <Typography.Text
                strong
                style={{
                  width: "30%",
                  fontSize: "20px",
                  padding: "7px",
                  backgroundColor: "#f9fafb",
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  textAlign: "center",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                }}
              >
                Fare : {totalFare || 0}
              </Typography.Text>
            </Flex>
          </Flex>
        </Flex>

        <Flex style={{ width: "100%" }}>
          <Flex style={{ width: "70%", gap: "1%" }}>
            {/* <DatePicker
              value={fromDate}
              onChange={(date) => setFromDate(date)}
              format="DD/MM/YYYY"
              placeholder="From Date"
              style={{ height: "70%" }}
            />
            <DatePicker
              value={toDate}
              onChange={(date) => setToDate(date)}
              format="DD/MM/YYYY"
              placeholder="To Date"
              style={{ height: "70%" }}
            /> */}
            <DatePicker
              value={fromDate}
              format="DD/MM/YYYY"
              onChange={(date) => {
                setFromDate(date);
                if (toDate && date && toDate.isBefore(date, "day")) {
                  setToDate(null);
                }
              }}
              disabledDate={disableFutureDates}
              style={{ border: "1px solid grey", height: "70%", width: "20%" }}
              placeholder="From Date"
            />

            <DatePicker
              value={toDate}
              format="DD/MM/YYYY"
              onChange={(date) => setToDate(date)}
              disabledDate={disableToDate}
              style={{ border: "1px solid grey", height: "70%", width: "20%" }}
              placeholder="To Date"
            />
            {/* <Input
              placeholder="Filter by CompanyName"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              style={{ width: "20%" }}
            /> */}
            <Form.Item
              name="companyName"
              rules={[{ required: true, message: "Please select Company" }]}
            >
              <Select
                placeholder="Select Company"
                value={nameFilter}
                onChange={(value) => setNameFilter(value)}
                className="compact-select"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {select
                  ?.filter(
                    (item: any) =>
                      item.companyname &&
                      item.companyname.trim() !== "" &&
                      item.companyname !== "-"
                  )
                  .map((item: any) => (
                    <Option key={item.id} value={item.companyname}>
                      {item.companyname}
                    </Option>
                  ))}
              </Select>
            </Form.Item>

            <Button
              style={{
                color: "white",
                background: "#800080",
                fontWeight: 500,
                width: "10%",
              }}
              onClick={handleFetch}
            >
              Go
            </Button>
            <Button
              style={{
                background: "linear-gradient(to right, rgb(136, 66, 153) 0%, rgb(158 54 124) 100%)",
                color: "white",
                marginLeft: "20px",
              }}
              onClick={handleToday}
            >
              Today  
            </Button>
            <Button
              style={{
                background: "linear-gradient(to right, rgb(136, 66, 153) 0%, rgb(158 54 124) 100%)",
                color: "white",
              }}
              onClick={handlePrevious}
            >
              Previous
            </Button>
            <Button
              style={{
                background: "linear-gradient(to right, rgb(136, 66, 153) 0%, rgb(158 54 124) 100%)",
                color: "white",
              }}
              onClick={handleMonth}
            >
              Month
            </Button>
          </Flex>
          <Flex style={{ width: "35%", gap: "2%", justifyContent: "end" }}>
            <Button
              icon={<DownloadOutlined />}
              onClick={() =>
                exportToExcel(filteredDataSource, "CorporateAccountData")
              }
              disabled={filteredDataSource.length === 0}
              style={{
                background: "linear-gradient(45deg, #159957, #155799)",
                color: "white",
              }}
            >
              Download
            </Button>
            <Input.Search
              placeholder="Search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={(value) => handleSearch(value)}
              style={{ width: "55%" }}
            />
          </Flex>
        </Flex>
        <Table
          rowClassName={(record, index) =>
            index % 2 === 0 ? "table-row-light" : "table-row-dark"
          }
          loading={{
            spinning: loading,
            indicator: <CustomTableLoader />,
          }}
          size="middle"
          pagination={{
            // pageSize: limit,
            // current: skip / limit + 1,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            defaultPageSize: 100,
            // onChange: (page: number, pageSize: number) => {
            //   setLimit(pageSize);
            //   setSkip((page - 1) * pageSize);
            // },
            // onShowSizeChange: (current: number, size: number) => {
            //   setLimit(size);
            //   setSkip(0);
            // },
          }}
          dataSource={filteredDataSource}
          columns={columns}
          scroll={{ x: "calc(1700px + 20%)", y: 50 * 15 }}
          bordered
          rowKey="bookingId"
        />
      </Flex>
    </Layout>
  );
};

export default Trip;
